import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { cn } from '@/lib/utils';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  allowStyle?: boolean;
}

export const MarkdownRenderer = ({ content, className, allowStyle = false }: MarkdownRendererProps) => {
  // Fix: Pre-process content to un-indent HTML lines that might be mistakenly parsed as code blocks
  // This happens when users paste HTML with indentation (4 spaces or tabs)
  // We look for lines starting with 4+ spaces followed by <tag, <!, or </
  const processedContent = content ? content.replace(/^[\t ]{4,}(<[a-zA-Z!/])/gm, '$1') : "";

  // Custom sanitization schema to allow specific HTML for layout (RTL) and styling
  const schema = {
    ...defaultSchema,
    attributes: {
      ...defaultSchema.attributes,
      // Allow global attributes on ALL tags
      // Added 'class' specifically because pasted HTML often uses 'class' instead of 'className'
      '*': ['className', 'class', 'style', 'id', 'dir', 'lang', 'align'],
      
      // Specific attributes for tags
      img: ['src', 'alt', 'title', 'width', 'height', 'align', 'className', 'class'],
      a: ['href', 'title', 'target', 'rel', 'className', 'class'],
      code: ['className', 'class'],
      style: [], // Allow style tag attributes if needed
    },
    tagNames: [
      ...(defaultSchema.tagNames || []),
      // Layout & Typography
      'div', 'span', 'img', 'br', 'hr', 'u', 's', 'kbd', 'small', 'big',
      // Semantic HTML
      'section', 'article', 'header', 'footer', 'nav', 'aside', 'main', 'figure', 'figcaption',
      // Style (conditional)
      ...(allowStyle ? ['style'] : [])
    ],
  };

  return (
    <div className={cn("markdown-content prose prose-slate max-w-none", className)} dir="rtl">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeRaw, 
          [rehypeSanitize, schema]
        ]}
        components={{
          // Force external links to open in new tab securely
          a: ({ node, ...props }) => {
            const isExternal = props.href?.startsWith('http');
            return (
              <a 
                {...props} 
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                className="text-blue-600 hover:underline"
              />
            );
          },
          // styling for tables
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4">
              <table {...props} className="w-full border-collapse border border-slate-200 text-sm" />
            </div>
          ),
          th: ({ node, ...props }) => <th {...props} className="border border-slate-200 bg-slate-50 p-2 text-right font-semibold" />,
          td: ({ node, ...props }) => <td {...props} className="border border-slate-200 p-2 text-right" />,
          // Fix direction for block elements if not specified
          p: ({ node, ...props }) => <p {...props} className="leading-relaxed mb-4" />,
          
          // Image handling with Shields.io fix
          img: ({ node, ...props }) => {
            let src = props.src || '';
            
            // Fix for Safari/iOS rendering of Hebrew text in SVGs from shields.io
            // Force PNG format instead of SVG by appending .png before query params
            if (src.startsWith('https://img.shields.io/badge/') && !src.includes('.png')) {
               const parts = src.split('?');
               const baseUrl = parts[0];
               const query = parts[1] ? `?${parts[1]}` : '';
               
               if (!baseUrl.endsWith('.png')) {
                 src = `${baseUrl}.png${query}`;
               }
            }

            return (
              <img 
                {...props} 
                src={src} 
                className={cn("max-w-full h-auto object-contain my-4 rounded-lg", props.className)} 
                loading="lazy"
              />
            );
          },
          
          blockquote: ({ node, ...props }) => <blockquote {...props} className="border-r-4 border-slate-300 pr-4 py-1 my-4 text-slate-600 italic bg-slate-50/50" />,
          code: ({ node, className, children, ...props }: any) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !match && !String(children).includes('\n');
            return isInline ? (
              <code {...props} className="bg-slate-100 px-1.5 py-0.5 rounded text-sm font-mono text-red-600">
                {children}
              </code>
            ) : (
              <pre className="bg-slate-900 text-slate-50 p-4 rounded-xl overflow-x-auto text-sm font-mono my-4" dir="ltr">
                <code {...props} className={className}>
                  {children}
                </code>
              </pre>
            );
          }
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};